import { useRef, useState } from "react";
import { Camera, X } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { PhotoEditor } from "@/components/PhotoEditor";

export const IntakePhotos = ({ files, onChange, testPrefix = "intake" }) => {
  const inputRef = useRef(null);
  const [previews, setPreviews] = useState([]);
  const [editIndex, setEditIndex] = useState(null);

  const add = (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    onChange([...files, ...picked]);
    setPreviews((p) => [...p, ...picked.map((f) => URL.createObjectURL(f))]);
    e.target.value = "";
  };

  const remove = (i) => {
    onChange(files.filter((_, j) => j !== i));
    setPreviews((p) => p.filter((_, j) => j !== i));
  };

  return (
    <div
      className="rounded-lg border border-zinc-800 bg-zinc-900 p-4"
      data-testid={`${testPrefix}-photos`}
    >
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-zinc-500">
            Photos
          </div>
          <p className="mt-1 text-xs text-zinc-500">
            Snap it now — photos upload as soon as you save.
          </p>
        </div>
        <Button
          type="button"
          data-testid={`${testPrefix}-add-photo`}
          variant="outline"
          onClick={() => inputRef.current?.click()}
          className="h-11 shrink-0 border-zinc-700 bg-transparent text-xs font-semibold uppercase tracking-widest hover:bg-zinc-800"
        >
          <Camera size={16} weight="bold" className="mr-2" /> Add Photo
        </Button>
        <input
          ref={inputRef}
          data-testid={`${testPrefix}-photo-input`}
          type="file"
          accept="image/*"
          capture="environment"
          multiple
          onChange={add}
          className="hidden"
        />
      </div>

      {previews.length > 0 && (
        <div className="mt-4 grid grid-cols-3 gap-3 sm:grid-cols-4">
          {previews.map((src, i) => (
            <div
              key={i}
              className="relative overflow-hidden rounded-md border border-zinc-800"
            >
              <img src={src} alt="" className="h-24 w-full object-cover" />
              <button
                type="button"
                data-testid={`${testPrefix}-edit-${i}`}
                onClick={() => setEditIndex(i)}
                className="absolute bottom-1 left-1 rounded-full bg-black/70 px-2 py-1 text-[9px] font-bold uppercase tracking-widest text-white hover:bg-yellow-500"
              >
                Edit
              </button>
              <button
                type="button"
                data-testid={`${testPrefix}-remove-${i}`}
                onClick={() => remove(i)}
                className="absolute right-1 top-1 rounded-full bg-black/70 p-1 text-white hover:bg-red-600"
              >
                <X size={12} weight="bold" />
              </button>
            </div>
          ))}
        </div>
      )}

      {editIndex !== null && files[editIndex] && (
        <PhotoEditor
          file={files[editIndex]}
          onDone={(f) => {
            onChange(files.map((x, j) => (j === editIndex ? f : x)));
            setPreviews((p) =>
              p.map((x, j) => (j === editIndex ? URL.createObjectURL(f) : x)),
            );
            setEditIndex(null);
          }}
          onClose={() => setEditIndex(null)}
        />
      )}
    </div>
  );
};
